import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import { registerAuditLabels, type StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo programs (ver modules/programs/README.md). */

export type ProgramKind = "formacao" | "workshop" | "mentoria" | "avaliacao_360" | "evento";
export type Modality = "presencial" | "online" | "hibrido";
export type ClassStatus = "draft" | "open" | "closed" | "in_progress" | "completed" | "canceled";
export type EnrollmentStatus = "confirmed" | "waitlisted" | "canceled" | "completed" | "not_completed";

export const KIND_LABEL: Record<ProgramKind, string> = {
  formacao: "Formação (turma)",
  workshop: "Workshop in-company",
  mentoria: "Mentoria",
  avaliacao_360: "Avaliação 360°",
  evento: "Evento aberto",
};
export const MODALITY_LABEL: Record<Modality, string> = { presencial: "Presencial", online: "Online", hibrido: "Híbrido" };

export const CLASS_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  open: { label: "inscrições abertas", tone: "info" },
  closed: { label: "inscrições fechadas", tone: "warn" },
  in_progress: { label: "em andamento", tone: "info" },
  completed: { label: "encerrada", tone: "ok" },
  canceled: { label: "cancelada", tone: "neutral" },
};
export const ENROLLMENT_STATUS: StatusMap = {
  confirmed: { label: "confirmada", tone: "info" },
  waitlisted: { label: "lista de espera", tone: "warn" },
  canceled: { label: "cancelada", tone: "neutral" },
  completed: { label: "concluída", tone: "ok" },
  not_completed: { label: "não concluída", tone: "crit" },
};
export const SESSION_STATUS: StatusMap = {
  scheduled: { label: "agendada", tone: "info" },
  done: { label: "realizada", tone: "ok" },
  canceled: { label: "cancelada", tone: "neutral" },
};

export interface Competency {
  id: string;
  org_id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
}

export interface Program {
  id: string;
  org_id: string;
  slug: string;
  kind: ProgramKind;
  title: string;
  summary: string | null;
  description: string | null;
  modality: Modality;
  workload_hours: number;
  min_attendance_pct: number;
  requires_approval: boolean;
  min_score: number | null;
  price_cents: number;
  offer_slug: string | null;
  issuer_id: string | null;
  certificate_enabled: boolean;
  certificate_template_key: string | null;
  consent_purpose: string;
  is_public: boolean;
  status: "active" | "archived";
  program_competencies?: Array<{ competency_id: string }>;
}

export interface ClassView {
  id: string;
  org_id: string;
  program_id: string;
  program_slug: string;
  program_title: string;
  program_kind: ProgramKind;
  code: string;
  title: string;
  modality: Modality;
  location: string | null;
  online_url: string | null;
  capacity: number | null;
  waitlist_enabled: boolean;
  price_cents: number;
  price_overridden: boolean;
  enrollment_opens_at: string | null;
  enrollment_closes_at: string | null;
  is_public: boolean;
  status: ClassStatus;
  instructor_person_id: string | null;
  instructor_name: string | null;
  reminder_hours_before: number;
  notes: string | null;
  cancel_reason: string | null;
  confirmed_count: number;
  waitlist_count: number;
  seats_left: number | null;
  starts_at: string | null;
  ends_at: string | null;
  total_hours: number;
}

export interface Session {
  id: string;
  class_id: string;
  number: number;
  starts_at: string;
  ends_at: string;
  hours: number;
  topic: string | null;
  location: string | null;
  online_url: string | null;
  status: "scheduled" | "done" | "canceled";
  reminder_sent_at: string | null;
}

export interface EnrollmentView {
  id: string;
  org_id: string;
  class_id: string;
  class_code: string;
  class_title: string;
  program_id: string;
  program_title: string;
  person_id: string;
  person_name: string | null;
  person_email: string | null;
  person_phone: string | null;
  person_company: string | null;
  status: EnrollmentStatus;
  source: "site" | "team";
  price_cents: number;
  waitlist_position: number | null;
  confirmed_at: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
  completed_at: string | null;
  approved: boolean | null;
  score: number | null;
  hours_total: number;
  hours_attended: number;
  attendance_pct: number;
  created_at: string;
}

export interface AttendanceCurrent {
  session_id: string;
  enrollment_id: string;
  present: boolean;
  note: string | null;
  recorded_at: string;
}

export interface Evaluation {
  met: boolean;
  reasons: string[];
  hours_total: number;
  hours_attended: number;
  attendance_pct: number;
  sessions_total: number;
  sessions_present: number;
  outcome?: "completed" | "not_completed";
}

export interface PublicClass {
  class_code: string;
  class_title: string;
  program_slug: string;
  program_title: string;
  kind: ProgramKind;
  summary: string | null;
  modality: Modality;
  location: string | null;
  starts_at: string | null;
  ends_at: string | null;
  total_hours: number;
  capacity: number | null;
  seats_left: number | null;
  waitlist_enabled: boolean;
  enrollment_open: boolean;
  sessions: Array<{ number: number; starts_at: string; ends_at: string; topic: string | null }>;
}

export function useCompetencies(orgId: string) {
  return useQuery({
    queryKey: ["programs", "competencies", orgId],
    queryFn: () => unwrap<Competency[]>(from("programs", "competencies").select("*").eq("org_id", orgId).order("name")),
  });
}

export function usePrograms(orgId: string) {
  return useQuery({
    queryKey: ["programs", "programs", orgId],
    queryFn: () =>
      unwrap<Program[]>(from("programs", "programs").select("*, program_competencies(competency_id)").eq("org_id", orgId).order("title")),
  });
}

/** "06/10/2026" quando começa e termina no mesmo dia; senão "01/10/2026 a 30/11/2026". */
export function formatPeriod(start: string | null | undefined, end: string | null | undefined, fmt: (v: string) => string): string {
  if (!start) return "";
  const a = fmt(start);
  const b = end ? fmt(end) : a;
  return a === b ? a : `${a} a ${b}`;
}

/** "1,5 h", "24 h". */
export function formatHours(h: number | null | undefined): string {
  if (h === null || h === undefined) return "";
  const n = Number(h);
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0$/, "").replace(".", ",")} h`;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "programs.upsert_competency": {
      args: { p_org: string; p_code: string; p_name: string; p_description?: string | null; p_active?: boolean; p_id?: string | null };
      returns: string;
    };
    "programs.upsert_program": {
      args: {
        p_org: string; p_slug: string; p_kind: ProgramKind; p_title: string; p_summary?: string | null; p_description?: string | null;
        p_modality?: Modality; p_workload_hours?: number; p_min_attendance_pct?: number; p_requires_approval?: boolean;
        p_min_score?: number | null; p_price_cents?: number; p_offer_slug?: string | null; p_issuer?: string | null;
        p_certificate_enabled?: boolean; p_certificate_template_key?: string | null; p_is_public?: boolean;
        p_consent_purpose?: string; p_competencies?: string[] | null; p_id?: string | null;
      };
      returns: string;
    };
    "programs.archive_program": { args: { p_program: string }; returns: null };
    "programs.upsert_class": {
      args: {
        p_program: string; p_code: string; p_title: string; p_modality?: Modality | null; p_location?: string | null;
        p_online_url?: string | null; p_capacity?: number | null; p_waitlist_enabled?: boolean; p_price_cents?: number | null;
        p_enrollment_opens_at?: string | null; p_enrollment_closes_at?: string | null; p_is_public?: boolean;
        p_instructor?: string | null; p_reminder_hours_before?: number; p_notes?: string | null; p_id?: string | null;
      };
      returns: string;
    };
    "programs.set_class_status": { args: { p_class: string; p_status: ClassStatus; p_reason?: string | null }; returns: null };
    "programs.upsert_session": {
      args: { p_class: string; p_starts_at: string; p_ends_at: string; p_topic?: string | null; p_location?: string | null; p_online_url?: string | null; p_id?: string | null };
      returns: string;
    };
    "programs.cancel_session": { args: { p_session: string; p_reason: string }; returns: null };
    "programs.enroll": {
      args: { p_class: string; p_person: string; p_price_cents?: number | null; p_idempotency_key?: string | null };
      returns: { enrollment_id: string; status: EnrollmentStatus; waitlist_position: number | null; duplicate: boolean };
    };
    "programs.enroll_public": {
      args: {
        p_org_slug: string; p_class_code: string; p_name: string; p_email: string; p_phone?: string | null; p_company?: string | null;
        p_job_title?: string | null; p_consent: boolean; p_newsletter?: boolean; p_idempotency_key?: string | null;
      };
      returns: { enrollment_id: string; status: EnrollmentStatus; waitlist_position: number | null; duplicate: boolean; class_title: string; program_title: string };
    };
    "programs.cancel_enrollment": { args: { p_enrollment: string; p_reason: string }; returns: null };
    "programs.promote": { args: { p_class: string }; returns: string };
    "programs.record_attendance": { args: { p_session: string; p_enrollment: string; p_present: boolean; p_note?: string | null }; returns: string };
    "programs.record_attendance_bulk": { args: { p_session: string; p_entries: Array<{ enrollment_id: string; present: boolean; note?: string }> }; returns: number };
    "programs.set_assessment": { args: { p_enrollment: string; p_approved?: boolean | null; p_score?: number | null }; returns: null };
    "programs.check_completion": { args: { p_enrollment: string }; returns: Evaluation };
    "programs.complete_enrollment": { args: { p_enrollment: string }; returns: Evaluation };
    "programs.close_class": { args: { p_class: string }; returns: { completed: number; not_completed: number } };
    "programs.public_agenda": { args: { p_org_slug: string }; returns: PublicClass[] };
  }
}

registerAuditLabels({
  "programs.competency_upserted": "Competência salva",
  "programs.program_upserted": "Programa salvo",
  "programs.program_archived": "Programa arquivado",
  "programs.class_upserted": "Turma salva",
  "programs.class_status": "Situação da turma alterada",
  "programs.class_closed": "Turma encerrada",
  "programs.session_upserted": "Sessão salva",
  "programs.session_canceled": "Sessão cancelada",
  "programs.enrollment_created": "Inscrição registrada",
  "programs.enrollment_promoted": "Vaga liberada da lista de espera",
  "programs.enrollment_canceled": "Inscrição cancelada",
  "programs.enrollment_completed": "Inscrição concluída",
  "programs.enrollment_not_completed": "Inscrição não concluída",
  "programs.attendance_recorded": "Presença registrada",
  "programs.assessment_set": "Avaliação registrada",
});
